import keys from '@debbie/common/dist/cache/keys.js'
import * as performedBy from '@debbie/common/dist/performed-by/index.js'
import { and, eq, or, query } from '@debbie/common/dist/query/index.js'
import { useMutation } from '@debbie/common/dist/util/hooks.js'
import { ViewVisibilityType } from '@debbie/common/dist/views/visibility.js'
import * as viewsApi from '@debbie/cortex/dist/api/views.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useMe } from '@debbie/cortex/dist/me/index.js'
import q from '@debbie/cortex/dist/query.js'
import { useQuery } from '@debbie/cortex/dist/query/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { viewsEntity } from '@debbie/cortex/dist/views/index.js'
import noData from '@debbie/iris/dist/assets/images/no-data.svg'
import Disclaimer from '@debbie/iris/dist/components/Disclaimer.js'
import {
  Check,
  HelpCircle,
  Plus,
  Star,
  X,
} from '@debbie/iris/dist/components/Icons/index.js'
import {
  Button,
  Content,
  Loading,
  PageBase,
  Panel,
  TopNav,
} from '@debbie/iris/dist/components/index.js'
import THEME from '@debbie/iris/dist/theme.js'
import { errorNotification } from '@debbie/iris/dist/utils/errors.js'
import React from 'react'
import { useSelector } from 'react-redux'

import { View } from './View.js'

type Props = {}

export const ViewsPage = (props: Props) => {
  const lang = useSelector((s: RootState) => s.lang)

  return (
    <>
      <TopNav />
      <Content>
        <PageBase>
          <Disclaimer>
            <p>{lang.views.betaDisclaimer}</p>
          </Disclaimer>
          <ViewPanel visibility={ViewVisibilityType.Individual} />
          <ViewPanel visibility={ViewVisibilityType.Public} />
        </PageBase>
      </Content>
    </>
  )
}

const ViewPanel = (props: { visibility: ViewVisibilityType }) => {
  const lang = useSelector((s: RootState) => s.lang)
  const meLoader = useMe()

  const viewsQuery = query('views')({
    base: 'views',
    properties: {
      favoritedViews: {
        fields: ['id'],
        markUnique: true,
        filters: eq(
          'favoritedViews.userId',
          h.hasData(meLoader) ? meLoader.data.id : '',
        ),
      },
      views: {
        fields: [
          'id',
          'name',
          'visibility',
          'options',
          'createdAt',
          'createdBy',
          'updatedAt',
          'updatedBy',
        ],
      },
    },
    orderBy: 'createdAt',
    filters:
      props.visibility === ViewVisibilityType.Individual
        ? and(
            eq('views.visibility', props.visibility),
            eq(
              'views.createdById',
              h.hasData(meLoader) ? meLoader.data.id : '',
            ),
          )
        : eq('views.visibility', props.visibility),
    // These are changed by view
    limit: 0,
    offset: 0,
  })

  const creating = useMutation(viewsApi.create, {
    onSuccess: (res) => {
      c.redirect.redirect(`/views/${res.id}`)
      q.invalidate(keys.write.create(viewsEntity))
    },
    onError: (err) => {
      errorNotification(lang, err, 'create', lang.errorEntities.view)
    },
  })

  return (
    <>
      <Panel
        header={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              <div>
                {props.visibility === ViewVisibilityType.Individual
                  ? 'Personlige'
                  : 'Delte'}{' '}
                {lang.navigation.views}
              </div>
              <a
                style={{ lineHeight: '1', margin: '0px' }}
                href="https://intercom-help.eu/debbie-eu/da/articles/596991-visninger"
              >
                <HelpCircle
                  style={{
                    width: '16px',
                    height: '16px',
                    flexShrink: 0,
                    margin: 0,
                    verticalAlign: 'bottom',
                    color: THEME.COLOR_GREY,
                  }}
                ></HelpCircle>
              </a>
            </div>
            <Button
              loading={creating.isLoading}
              disabled={creating.isLoading}
              size="mini"
              onClick={() => {
                creating.mutate({
                  name: '',
                  visibility: props.visibility,
                  options: {
                    base: 'cases',
                    displayOptions: [
                      'cases.id',
                      'creditors.name',
                      'cases.status',
                      'contactName',
                      'caseGroups.label',
                      'cases.createdAt',
                      'cases.createdBy',
                    ],
                    filters: [],
                    orderByDirection: 'DESC',
                    orderBy: 'createdAt',
                  },
                })
              }}
            >
              +
            </Button>
          </div>
        }
      >
        <View<typeof viewsQuery.base, typeof viewsQuery>
          base={viewsQuery.base}
          query={viewsQuery}
          options={{
            view: [
              {
                value: 'favoritedViews.id',
                width: '30px',
                label: (lang) => '',
                component: (props) => {
                  const favoriting = useMutation(
                    props.value.favoritedViews?.id != null
                      ? viewsApi.unfavorite
                      : viewsApi.favorite,
                    {
                      onSuccess: (res) => {
                        q.invalidate(keys.write.create(viewsEntity))
                      },
                      onError: (err) => {
                        errorNotification(
                          lang,
                          err,
                          'update',
                          lang.errorEntities.view,
                        )
                      },
                    },
                  )

                  return (
                    <div
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        if (
                          !(favoriting.isLoading || favoriting.data != null)
                        ) {
                          favoriting.mutate(props.value.views.id)
                        }
                      }}
                    >
                      {favoriting.isLoading || favoriting.data != null ? ( // favoriting.data is set after mutation, but the entire component is replaced when the view reloads
                        <Loading width="1em" />
                      ) : props.value.favoritedViews?.id ? (
                        <Star fill="true" />
                      ) : (
                        <Star />
                      )}
                    </div>
                  )
                },
              },
              {
                value: 'views.name',
                label: (lang) => lang.views.name,
                width: '30%',
                component: (props) => {
                  if (props.value.views.name === '') {
                    return (
                      <span style={{ color: 'gray' }}>{lang.misc.unnamed}</span>
                    )
                  }
                  return props.value.views.name
                },
              },
              /*{
              values: ['views.options'],
              label: (lang) => 'Base',
              component: (props) => {
                return props.value.views.options.base
              },
            },*/
              {
                value: 'views.createdAt',
                label: (lang) => lang.misc.createdAt,
              },
              {
                value: 'views.createdBy',
                label: (lang) => lang.misc.createdBy,
              },
              {
                value: 'views.updatedAt',
                label: (lang) => lang.misc.updatedAt,
              },
              {
                value: 'views.updatedBy',
                label: (lang) => lang.misc.updatedBy,
              },
              {
                values: [],
                width: '30px',
                label: (lang) => '',
                component: (props) => {
                  const deletion = useMutation(viewsApi.deleteById, {
                    onSuccess: (response, id) => {
                      q.invalidate(keys.write.modify(viewsEntity, id))
                    },
                    onError: (err) => {
                      errorNotification(
                        lang,
                        err,
                        'delete',
                        lang.errorEntities.view,
                      )
                    },
                  })

                  return (
                    <div
                      style={{
                        padding: '1em',
                      }}
                    >
                      {deletion.isLoading ? (
                        <Loading width="1em" />
                      ) : (
                        <X
                          data-tooltip-id="tooltip-default"
                          data-tooltip-content={lang.misc.delete}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            deletion.mutate(props.value.views.id)
                          }}
                        />
                      )}
                    </div>
                  )
                },
              },
            ],
          }}
          onClick={(item) => {
            c.redirect.redirect(`/views/${item.views.id}`)
          }}
          emptyComponent={(props) => {
            return (
              <>
                <img src={noData} style={{ maxWidth: '200px' }}></img>
                <h2>{lang.views.introHeader}</h2>
                <p>{lang.views.introDescription} </p>
                <a href="https://intercom-help.eu/debbie-eu/da/articles/596991-visninger">
                  {lang.misc.learnMore}
                </a>
              </>
            )
          }}
        />
      </Panel>
    </>
  )
}
